import React, { useState, useRef, useEffect } from 'react';
import { Bot, X, Send, FileText, FileSearch, Copy, Download, Layers, BookOpen, CheckSquare, List, HelpCircle, ChevronDown } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

// ============================================
// Document AI Panel
// ============================================
// Slide-over panel for document AI interactions.
// Supports: Q&A, derivative creation, cross-reference.
// ============================================

const DERIVATIVE_TYPES = [
    { value: 'summary', label: 'Executive Summary', icon: FileText },
    { value: 'one_pager', label: 'One-Pager', icon: FileSearch },
    { value: 'checklist', label: 'Checklist', icon: CheckSquare },
    { value: 'training_outline', label: 'Training Outline', icon: BookOpen },
    { value: 'key_points', label: 'Key Points', icon: List },
    { value: 'faq', label: 'FAQ', icon: HelpCircle },
];

const DocumentAIPanel = ({ isOpen, onClose, file, allFiles = [], onRouteToCreator }) => {
    const [activeTab, setActiveTab] = useState('qa'); // qa | derivative | compare
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [derivativeResult, setDerivativeResult] = useState(null);
    const [selectedDerivativeType, setSelectedDerivativeType] = useState('summary');
    const [compareFileId, setCompareFileId] = useState('');
    const [compareResult, setCompareResult] = useState(null);
    const [showDerivativeDropdown, setShowDerivativeDropdown] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    useEffect(() => {
        if (isOpen) inputRef.current?.focus();
    }, [isOpen, activeTab]);

    // Reset state when file changes
    useEffect(() => {
        setMessages([]);
        setDerivativeResult(null);
        setCompareResult(null);
    }, [file?.id]);

    async function sendQAMessage(e) {
        if (e) e.preventDefault();
        const question = inputMessage.trim();
        if (!question || !file) return;

        setInputMessage('');
        setMessages(prev => [...prev, { role: 'user', content: question }]);
        setIsSending(true);

        try {
            const response = await api.post(`/files/ai/document/${file.id}/ask`, {
                question,
                chat_history: messages.map(m => ({ role: m.role, content: typeof m.content === 'string' ? m.content : m.content.answer })),
            });

            if (response.data.success) {
                setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: response.data.response,
                }]);
            }
        } catch (error) {
            console.error('Document Q&A error:', error);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: { answer: 'Something went wrong. Please try again.', follow_up_questions: [] },
            }]);
        } finally {
            setIsSending(false);
        }
    }

    async function generateDerivative() {
        if (!file) return;
        setIsSending(true);
        setDerivativeResult(null);

        try {
            const response = await api.post(`/files/ai/document/${file.id}/derivative`, {
                derivative_type: selectedDerivativeType,
            });

            if (response.data.success) {
                setDerivativeResult(response.data.derivative);
            }
        } catch (error) {
            console.error('Derivative error:', error);
            toast.error('Failed to generate derivative');
        } finally {
            setIsSending(false);
        }
    }

    async function runComparison() {
        if (!file || !compareFileId) return;
        setIsSending(true);
        setCompareResult(null);

        try {
            const response = await api.post('/files/ai/documents/compare', {
                file_id_1: file.id,
                file_id_2: parseInt(compareFileId),
            });

            if (response.data.success) {
                setCompareResult(response.data);
            }
        } catch (error) {
            console.error('Compare error:', error);
            toast.error('Failed to compare documents');
        } finally {
            setIsSending(false);
        }
    }

    function copyToClipboard(text) {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    }

    if (!isOpen || !file) return null;

    const otherFiles = allFiles.filter(f => f.id !== file.id && f.ai_indexed);

    return (
        <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-card border-l border-border shadow-2xl z-50 flex flex-col animate-slide-in-right">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-cyan-600 to-teal-600 text-white">
                <div className="flex items-center gap-2 min-w-0">
                    <FileSearch size={20} />
                    <div className="min-w-0">
                        <span className="font-semibold text-sm block">Document AI</span>
                        <span className="text-xs text-white/70 truncate block">{file.file_name}</span>
                    </div>
                </div>
                <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/20 transition-colors cursor-pointer">
                    <X size={18} />
                </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-border bg-background">
                {[
                    { id: 'qa', label: 'Ask', icon: HelpCircle },
                    { id: 'derivative', label: 'Create', icon: FileText },
                    { id: 'compare', label: 'Compare', icon: Layers },
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-medium cursor-pointer transition-all border-b-2 ${activeTab === tab.id
                                ? 'border-cyan-500 text-cyan-600'
                                : 'border-transparent text-muted-foreground hover:text-foreground'
                            }`}
                    >
                        <tab.icon size={14} />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
                {/* Q&A Tab */}
                {activeTab === 'qa' && (
                    <div className="p-4 space-y-4 h-full flex flex-col">
                        <div className="flex-1 space-y-4 overflow-y-auto">
                            {messages.length === 0 && (
                                <div className="text-center py-8">
                                    <FileSearch size={32} className="text-cyan-400 mx-auto mb-3" />
                                    <p className="text-sm text-muted-foreground">Ask anything about <strong>{file.file_name}</strong></p>
                                    <div className="flex flex-wrap justify-center gap-2 mt-4">
                                        {['Summarize this document', 'What are the key points?', 'Who is this for?'].map((q, i) => (
                                            <button
                                                key={i}
                                                onClick={() => { setInputMessage(q); }}
                                                className="px-3 py-1.5 bg-card border border-border rounded-lg text-xs text-muted-foreground hover:text-foreground hover:border-cyan-300 cursor-pointer transition-all"
                                            >
                                                {q}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {messages.map((msg, index) => (
                                <div key={index} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                                    {msg.role === 'assistant' && (
                                        <div className="w-7 h-7 min-w-[28px] rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center text-white">
                                            <Bot size={14} />
                                        </div>
                                    )}
                                    <div className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${msg.role === 'user'
                                            ? 'bg-primary text-white rounded-br-md'
                                            : 'bg-muted/50 border border-border text-foreground rounded-bl-md'
                                        }`}>
                                        <p className="m-0 whitespace-pre-wrap">
                                            {typeof msg.content === 'string' ? msg.content : msg.content.answer}
                                        </p>
                                        {msg.content?.follow_up_questions?.length > 0 && (
                                            <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-border/30">
                                                {msg.content.follow_up_questions.map((q, i) => (
                                                    <button
                                                        key={i}
                                                        onClick={() => { setInputMessage(q); inputRef.current?.focus(); }}
                                                        className="px-2.5 py-1 bg-card border border-border rounded-lg text-xs text-muted-foreground hover:text-foreground cursor-pointer transition-all"
                                                    >
                                                        {q}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}

                            {isSending && (
                                <div className="flex gap-3">
                                    <div className="w-7 h-7 min-w-[28px] rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center text-white">
                                        <Bot size={14} />
                                    </div>
                                    <div className="flex items-center gap-2 px-4 py-3 bg-muted/50 border border-border rounded-2xl rounded-bl-md">
                                        <div className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-pulse"></div>
                                        <span className="text-sm text-muted-foreground italic">Reading document...</span>
                                    </div>
                                </div>
                            )}

                            <div ref={messagesEndRef} />
                        </div>
                    </div>
                )}

                {/* Derivative Tab */}
                {activeTab === 'derivative' && (
                    <div className="p-4 space-y-4">
                        <p className="text-sm text-muted-foreground">Create a derivative document from <strong>{file.file_name}</strong></p>

                        {/* Type selector */}
                        <div className="relative">
                            <button
                                onClick={() => setShowDerivativeDropdown(!showDerivativeDropdown)}
                                className="w-full flex items-center justify-between px-4 py-3 bg-background border border-border rounded-xl text-sm cursor-pointer hover:border-cyan-300 transition-all"
                            >
                                <div className="flex items-center gap-2">
                                    {(() => { const DIcon = DERIVATIVE_TYPES.find(d => d.value === selectedDerivativeType)?.icon || FileText; return <DIcon size={16} className="text-cyan-500" />; })()}
                                    <span className="text-foreground">{DERIVATIVE_TYPES.find(d => d.value === selectedDerivativeType)?.label}</span>
                                </div>
                                <ChevronDown size={16} className="text-muted-foreground" />
                            </button>
                            {showDerivativeDropdown && (
                                <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-lg z-10 overflow-hidden">
                                    {DERIVATIVE_TYPES.map(type => (
                                        <button
                                            key={type.value}
                                            onClick={() => { setSelectedDerivativeType(type.value); setShowDerivativeDropdown(false); }}
                                            className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm cursor-pointer transition-colors ${selectedDerivativeType === type.value ? 'bg-cyan-50 text-cyan-700' : 'hover:bg-muted/50 text-foreground'
                                                }`}
                                        >
                                            <type.icon size={14} />
                                            {type.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <button
                            onClick={generateDerivative}
                            disabled={isSending}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-cyan-500 to-teal-500 border-none rounded-xl font-medium text-sm text-white cursor-pointer transition-all hover:shadow-lg disabled:opacity-50"
                        >
                            {isSending ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    Generating...
                                </>
                            ) : (
                                <>
                                    <FileText size={16} />
                                    Generate {DERIVATIVE_TYPES.find(d => d.value === selectedDerivativeType)?.label}
                                </>
                            )}
                        </button>

                        {/* Result */}
                        {derivativeResult && (
                            <div className="bg-background border border-border rounded-xl p-4 space-y-3 animate-fade-in">
                                <div className="flex items-center justify-between">
                                    <h4 className="font-semibold text-foreground text-sm m-0">{derivativeResult.title}</h4>
                                    <div className="flex gap-1">
                                        <button
                                            onClick={() => copyToClipboard(derivativeResult.content)}
                                            className="p-1.5 rounded-lg hover:bg-muted cursor-pointer transition-colors"
                                            title="Copy"
                                        >
                                            <Copy size={14} className="text-muted-foreground" />
                                        </button>
                                    </div>
                                </div>
                                <div className="text-sm text-foreground leading-relaxed whitespace-pre-wrap max-h-96 overflow-y-auto">
                                    {derivativeResult.content}
                                </div>

                                {/* Route to creator if training outline */}
                                {selectedDerivativeType === 'training_outline' && onRouteToCreator && (
                                    <button
                                        onClick={() => onRouteToCreator('course', { description: derivativeResult.content, title: derivativeResult.title })}
                                        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700 cursor-pointer hover:bg-emerald-100 transition-colors"
                                    >
                                        <BookOpen size={14} />
                                        Create Course from This Outline
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Compare Tab */}
                {activeTab === 'compare' && (
                    <div className="p-4 space-y-4">
                        <p className="text-sm text-muted-foreground">Compare <strong>{file.file_name}</strong> with another document</p>

                        <select
                            value={compareFileId}
                            onChange={(e) => setCompareFileId(e.target.value)}
                            className="w-full px-4 py-3 bg-background border border-border rounded-xl text-sm text-foreground outline-none cursor-pointer"
                        >
                            <option value="">Select a document to compare...</option>
                            {otherFiles.map(f => (
                                <option key={f.id} value={f.id}>{f.file_name}</option>
                            ))}
                        </select>

                        <button
                            onClick={runComparison}
                            disabled={isSending || !compareFileId}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-cyan-500 to-teal-500 border-none rounded-xl font-medium text-sm text-white cursor-pointer transition-all hover:shadow-lg disabled:opacity-50"
                        >
                            {isSending ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    Comparing...
                                </>
                            ) : (
                                <>
                                    <Layers size={16} />
                                    Compare Documents
                                </>
                            )}
                        </button>

                        {compareResult && (
                            <div className="space-y-3 animate-fade-in">
                                <div className="bg-background border border-border rounded-xl p-4">
                                    <h4 className="font-semibold text-foreground text-sm mb-2">Summary</h4>
                                    <p className="text-sm text-foreground m-0">{compareResult.comparison.summary}</p>
                                </div>

                                {compareResult.comparison.overlap?.length > 0 && (
                                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                                        <h5 className="text-xs font-semibold text-blue-700 mb-1.5">Shared Topics</h5>
                                        <ul className="text-xs text-blue-600 m-0 pl-4 space-y-0.5">
                                            {compareResult.comparison.overlap.map((item, i) => <li key={i}>{item}</li>)}
                                        </ul>
                                    </div>
                                )}

                                {compareResult.comparison.contradictions?.length > 0 && (
                                    <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                                        <h5 className="text-xs font-semibold text-red-700 mb-1.5">Contradictions</h5>
                                        <ul className="text-xs text-red-600 m-0 pl-4 space-y-0.5">
                                            {compareResult.comparison.contradictions.map((item, i) => <li key={i}>{item}</li>)}
                                        </ul>
                                    </div>
                                )}

                                {compareResult.comparison.recommendation && (
                                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                                        <h5 className="text-xs font-semibold text-amber-700 mb-1">Recommendation</h5>
                                        <p className="text-xs text-amber-600 m-0">{compareResult.comparison.recommendation}</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Input (Q&A tab only) */}
            {activeTab === 'qa' && (
                <form className="flex gap-2 p-3 bg-card border-t border-border" onSubmit={sendQAMessage}>
                    <input
                        ref={inputRef}
                        type="text"
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        placeholder="Ask about this document..."
                        disabled={isSending}
                        className="flex-1 px-4 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-200 placeholder:text-muted-foreground disabled:opacity-50"
                    />
                    <button
                        type="submit"
                        disabled={!inputMessage.trim() || isSending}
                        className="w-10 h-10 rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 border-none text-white cursor-pointer flex items-center justify-center transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Send size={16} />
                    </button>
                </form>
            )}
        </div>
    );
};

export default DocumentAIPanel;
